import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, User, Users, Map, Clock, UserCheck } from 'lucide-react';
import quienesSomosService from '../services/quienesSomosService';
import raulPhoto from '../assets/funcionarios/raul_fuentes.png';
import jorgePhoto from '../assets/funcionarios/jorge_venegas.png';

export default function QuienesSomos() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await quienesSomosService.get();
      setData(res);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner spinner-lg"></div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="container" style={{ padding: '2rem 1rem', maxWidth: '1000px' }}>
      {/* Hero Section */}
      <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <h1 style={{
          fontFamily: 'Space Grotesk, sans-serif',
          color: 'var(--primary-dark)',
          fontSize: '2.5rem',
          fontWeight: '800',
          marginBottom: '1rem'
        }}>
          Quiénes Somos
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto' }}>
          Conozca más sobre nuestra institución, autoridades y jurisdicción correspondientes al Conservador de Bienes Raíces de {import.meta.env.VITE_CBR_CITY || "San Carlos"}.
        </p>
      </div>

      {/* Conservador y Contacto */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '2rem',
        marginBottom: '3rem'
      }}>
        <div className="glass-card" style={{ padding: '2rem', borderTop: '4px solid var(--primary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ padding: '12px', background: 'var(--primary-light)', color: 'white', borderRadius: '12px' }}>
              <User size={32} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--text-main)' }}>Conservador Interino</h2>
              <span style={{ fontSize: '0.9rem', color: 'var(--primary)', fontWeight: 600 }}>{import.meta.env.VITE_CBR_NAME || "CBR San Carlos"}</span>
            </div>
          </div>
          <p style={{ margin: 0 }}>
            <a 
              href={data.link_conservador} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ fontSize: '1.2rem', fontWeight: 600, color: 'var(--primary)', textDecoration: 'none', transition: 'color 0.2s' }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              {data.nombre_conservador}
            </a>
          </p>
          <hr style={{ margin: '1.5rem 0', borderColor: 'var(--border-color)', opacity: 0.5 }} />
          
          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <img 
              src={data.foto_url ? `${import.meta.env.VITE_API_ORIGIN}${data.foto_url}` : raulPhoto} 
              alt={data.nombre_conservador || 'Raúl Hernán Fuentes Jiménez'} 
              style={{ 
                width: '100%', 
                maxWidth: '320px', 
                borderRadius: '12px', 
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                border: '3px solid white'
              }} 
            />
          </div>
        </div>

        <div className="glass-card" style={{ padding: '2rem' }}>
          <h2 style={{ fontSize: '1.25rem', color: 'var(--text-main)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <MapPin size={24} color="var(--primary)" />
            Información de Contacto
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <li style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: 'var(--bg-body)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
              <div style={{ color: 'var(--primary)' }}><MapPin size={24} /></div>
              <div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dirección</div>
                <div style={{ fontWeight: 500, color: 'var(--text-main)' }}>{data.direccion}</div>
              </div>
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: 'var(--bg-body)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
              <div style={{ color: 'var(--primary)' }}><Phone size={24} /></div>
              <div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Teléfonos</div>
                <div style={{ fontWeight: 500, color: 'var(--text-main)' }}>{data.telefonos}</div>
              </div>
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: 'var(--bg-body)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
              <div style={{ color: 'var(--primary)' }}><Mail size={24} /></div>
              <div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Correo Electrónico</div>
                <div style={{ fontWeight: 500, color: 'var(--text-main)' }}>{data.email}</div>
              </div>
            </li>
            
            <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: 'var(--text-main)' }}>Horarios</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {data.horarios?.map((item, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <span style={{ color: 'var(--primary)', marginTop: '2px' }}><Clock size={20} /></span>
                    <div>
                      <strong style={{ display: 'block', fontSize: '0.95rem', color: 'var(--text-main)' }}>{item.label}</strong>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{item.time}</span>
                    </div>
                  </li>
              ))}
            </ul>
          </ul>
        </div>
      </div>

      {/* Jurisdicción y Suplentes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '2rem',
        marginBottom: '3rem'
      }}>
        <div className="glass-card" style={{ padding: '2rem' }}>
          <h2 style={{ fontSize: '1.25rem', color: 'var(--text-main)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Map size={24} color="var(--primary)" />
            Jurisdicción
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: '1rem' }}>
            Lugares de competencia territorial del {import.meta.env.VITE_CBR_NAME || "CBR San Carlos"}:
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {data.jurisdiccion?.map((lugar, idx) => (
              <span key={idx} style={{
                background: 'var(--primary-light)',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '20px',
                fontSize: '0.9rem',
                fontWeight: 500
              }}>
                {lugar}
              </span>
            ))}
          </div>
        </div>

        <div className="glass-card" style={{ padding: '2rem', display: 'flex', flexDirection: 'column' }}>
          <h2 style={{ fontSize: '1.25rem', color: 'var(--text-main)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <UserCheck size={24} color="var(--primary)" />
            Abogado reemplazante
          </h2>

          <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
            <img 
              src={data.suplentes?.[0]?.foto_url ? `${import.meta.env.VITE_API_ORIGIN}${data.suplentes[0].foto_url}` : jorgePhoto} 
              alt={data.suplentes?.[0]?.nombre || 'Jorge Eduardo Venegas Venegas'} 
              style={{ 
                width: '100%', 
                maxWidth: '210px', 
                borderRadius: '12px', 
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                border: '3px solid white',
                objectFit: 'cover'
              }} 
            />
          </div>

          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {data.suplentes?.map((suplente, idx) => (
              <li key={idx} style={{ paddingBottom: '0.5rem', borderBottom: idx !== data.suplentes.length - 1 ? '1px solid var(--border-color)' : 'none', textAlign: 'center' }}>
                <div style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: '1.1rem' }}>
                  {suplente.nombre}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                  {suplente.cargo}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Listado de Funcionarios */}
      <div className="glass-card" style={{ padding: '2rem' }}>
        <h2 style={{ fontSize: '1.25rem', color: 'var(--text-main)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Users size={24} color="var(--primary)" />
          Listado de Funcionarios y Cargos
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
          {data.funcionarios?.map((func, idx) => (
            <div key={idx} style={{ padding: '1.5rem', background: 'var(--bg-body)', borderRadius: '12px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ backgroundColor: 'var(--primary-light)', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '15px', fontSize: '0.85rem', fontWeight: 600 }}>
                  {func.nombre}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: '1.5' }}>
                {func.cargo}
              </p>
              {func.foto_url && (
                <div style={{ marginTop: '0.75rem' }}>
                  <img 
                    src={`${import.meta.env.VITE_API_ORIGIN}${func.foto_url}`} 
                    alt={func.nombre} 
                    style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--primary-light)' }} 
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
